import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, MessageCircle, Phone, MapPin, Mail, ArrowUpRight, UtensilsCrossed, Facebook, Instagram } from "lucide-react";
import { HOTEL } from "../../data";

const LINKS = [
  { label: "Rooms", href: "#rooms" },
  { label: "Suite", href: "#suite" },
  { label: "ROOFTOPz", href: "#rooftopz" },
  { label: "Café", href: "#cafe" },
  { label: "Banquet", href: "#banquet" },
  { label: "Gallery", href: "#gallery" },
  { label: "Location", href: "#location" },
  { label: "FAQ", href: "#faq" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
        scrolled ? "backdrop-blur-xl bg-[#F5F2ED]/80 border-b border-black/5" : "bg-transparent"
      }`}
    >
      <nav className="mx-auto max-w-[1400px] px-5 md:px-10 h-20 flex items-center justify-between">
        <a href="#top" data-testid="logo-link" className="flex items-center gap-3">
          <span className="h-11 w-11 rounded-full bg-[#F5F2ED] ring-1 ring-[#B89965]/40 flex items-center justify-center overflow-hidden shrink-0">
            <img src={HOTEL.logo} alt="Hotel Virasat Retreat logo" className="h-9 w-9 object-contain" />
          </span>
          <span className="flex flex-col leading-none">
            <span className={`font-display text-xl md:text-2xl tracking-tight ${scrolled ? "text-[#1A1A1A]" : "text-[#F5F2ED]"}`}>
              Hotel Virasat Retreat
            </span>
            <span className={`overline text-[10px] mt-1 ${scrolled ? "text-[#2C402E]" : "text-[#B89965]"}`}>
              Patna · Bihar
            </span>
          </span>
        </a>

        <div className="hidden lg:flex items-center gap-9">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={`text-sm relative group ${scrolled ? "text-[#1A1A1A]" : "text-[#F5F2ED]"}`}
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-[#B89965] transition-all duration-300 group-hover:w-full" />
            </a>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <a
            href="#enquiry"
            data-testid="header-book-direct"
            className={`btn-fill hidden sm:inline-flex items-center gap-2 text-sm px-5 py-2.5 rounded-full border ${
              scrolled
                ? "border-[#2C402E] text-[#2C402E] hover:text-[#F5F2ED]"
                : "border-[#F5F2ED] text-[#F5F2ED] hover:text-[#2C402E]"
            }`}
          >
            Book Direct <ArrowUpRight size={15} />
          </a>
          <button
            data-testid="menu-toggle"
            onClick={() => setOpen(true)}
            className={`lg:hidden ${scrolled ? "text-[#1A1A1A]" : "text-[#F5F2ED]"}`}
            aria-label="Open menu"
          >
            <Menu />
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-0 z-50 bg-[#2C402E] text-[#F5F2ED] p-8 flex flex-col"
            data-testid="mobile-menu"
          >
            <div className="flex justify-between items-center mb-16">
              <span className="flex items-center gap-3">
                <span className="h-10 w-10 rounded-full bg-[#F5F2ED] flex items-center justify-center overflow-hidden">
                  <img src={HOTEL.logo} alt="Hotel Virasat Retreat logo" className="h-8 w-8 object-contain" />
                </span>
                <span className="font-display text-xl">Hotel Virasat Retreat</span>
              </span>
              <button onClick={() => setOpen(false)} data-testid="menu-close" aria-label="Close menu">
                <X />
              </button>
            </div>
            <div className="flex flex-col gap-6">
              {LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="font-display text-4xl"
                  data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                >
                  {l.label}
                </a>
              ))}
            </div>
            <a
              href="#enquiry"
              onClick={() => setOpen(false)}
              className="mt-auto inline-flex items-center justify-center gap-2 border border-[#B89965] text-[#B89965] rounded-full py-4"
              data-testid="mobile-book-direct"
            >
              Book Direct <ArrowUpRight size={18} />
            </a>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export const WhatsAppWidget = () => (
  <a
    href={`https://wa.me/${HOTEL.whatsapp}?text=Hi%20Hotel%20Virasat%20Retreat%2C%20I%27d%20like%20to%20enquire%20about%20a%20room.`}
    target="_blank"
    rel="noopener noreferrer"
    data-testid="whatsapp-widget"
    className="fixed bottom-6 right-6 z-50 h-14 w-14 rounded-full bg-[#2C402E] text-[#F5F2ED] flex items-center justify-center shadow-xl hover:scale-110 transition-transform duration-300 ring-1 ring-[#B89965]/50"
    aria-label="Chat on WhatsApp"
  >
    <MessageCircle size={24} />
  </a>
);

export const Footer = () => (
  <footer className="bg-[#2C402E] text-[#F5F2ED]" data-testid="site-footer">
    <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-20">
      <div className="grid md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <span className="h-14 w-14 rounded-full bg-[#F5F2ED] flex items-center justify-center overflow-hidden mb-5">
            <img src={HOTEL.logo} alt="Hotel Virasat Retreat logo" className="h-11 w-11 object-contain" />
          </span>
          <h2 className="font-display text-4xl md:text-5xl leading-none">Hotel Virasat<br />Retreat</h2>
          <p className="mt-5 text-[#F5F2ED]/70 max-w-sm leading-relaxed">
            A family-owned 16-room boutique hotel opposite Medanta Super Speciality Hospital, in a serene residential area of Patna — with ROOFTOPz Botanical Restaurant and a 3rd-floor banquet.
          </p>
          <p className="mt-5 font-display italic text-xl text-[#B89965]">Traditional Hospitality, Modern Facilities.</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <a href={HOTEL.mapsUrl} target="_blank" rel="noopener noreferrer" data-testid="footer-directions" className="inline-flex items-center gap-2 border border-[#F5F2ED]/25 rounded-full px-4 py-2 text-sm hover:border-[#B89965] hover:text-[#B89965] transition-colors"><MapPin size={14} /> Directions</a>
            <a href={`https://wa.me/${HOTEL.whatsapp}`} target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp" className="inline-flex items-center gap-2 border border-[#F5F2ED]/25 rounded-full px-4 py-2 text-sm hover:border-[#B89965] hover:text-[#B89965] transition-colors"><MessageCircle size={14} /> WhatsApp</a>
            <a href={HOTEL.zomato} target="_blank" rel="noopener noreferrer" data-testid="footer-zomato" className="inline-flex items-center gap-2 border border-[#F5F2ED]/25 rounded-full px-4 py-2 text-sm hover:border-[#B89965] hover:text-[#B89965] transition-colors"><UtensilsCrossed size={14} /> Order on Zomato</a>
            <a href={HOTEL.facebook} target="_blank" rel="noopener noreferrer" data-testid="footer-facebook" aria-label="Facebook" className="inline-flex items-center gap-2 border border-[#F5F2ED]/25 rounded-full px-4 py-2 text-sm hover:border-[#B89965] hover:text-[#B89965] transition-colors"><Facebook size={14} /> Facebook</a>
            <a href={HOTEL.instagram} target="_blank" rel="noopener noreferrer" data-testid="footer-instagram" aria-label="Instagram" className="inline-flex items-center gap-2 border border-[#F5F2ED]/25 rounded-full px-4 py-2 text-sm hover:border-[#B89965] hover:text-[#B89965] transition-colors"><Instagram size={14} /> Instagram</a>
          </div>
        </div>
        <div className="md:col-span-3 md:col-start-7">
          <p className="overline text-[#B89965] mb-4">Explore</p>
          <ul className="space-y-2 text-[#F5F2ED]/80">
            {LINKS.map((l) => (
              <li key={l.href}><a href={l.href} className="hover:text-[#B89965] transition-colors">{l.label}</a></li>
            ))}
          </ul>
        </div>
        <div className="md:col-span-3">
          <p className="overline text-[#B89965] mb-4">Reach Us</p>
          <ul className="space-y-3 text-[#F5F2ED]/80 text-sm">
            <li className="flex gap-3"><MapPin size={16} className="mt-0.5 shrink-0 text-[#B89965]" />{HOTEL.addressLine}, {HOTEL.city}, {HOTEL.state} {HOTEL.pincode}</li>
            <li className="flex gap-3"><Phone size={16} className="shrink-0 text-[#B89965]" /><a href={`tel:${HOTEL.phone}`} className="hover:text-[#B89965]">{HOTEL.phoneDisplay}</a></li>
            <li className="flex gap-3"><MessageCircle size={16} className="shrink-0 text-[#B89965]" /><a href={`https://wa.me/${HOTEL.whatsapp}`} target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp-number" className="hover:text-[#B89965]">Chat on WhatsApp · +91 77650 90955</a></li>
            <li className="flex gap-3"><Mail size={16} className="shrink-0 text-[#B89965]" /><a href={`mailto:${HOTEL.email}`} className="hover:text-[#B89965]">{HOTEL.email}</a></li>
          </ul>
        </div>
      </div>
      <div className="mt-16 pt-8 border-t border-[#F5F2ED]/15 flex flex-col md:flex-row justify-between gap-4 text-xs text-[#F5F2ED]/50">
        <p>© {new Date().getFullYear()} Hotel Virasat Retreat, Patna. All rights reserved.</p>
        <p>Luxury hotel near Medanta Hospital · Kankarbagh · Patna, Bihar</p>
      </div>
    </div>
  </footer>
);
